/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

import { jsx, cssMap } from '@compiled/react';
import { useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { AvatarContent } from '@atlaskit/avatar/avatar-content';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages/ai-suggestions';
import { RovoHexIcon } from '@atlaskit/logo/rovo-hex/icon';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip/Tooltip';

import type { SuggestionProfile } from '../../../aiSuggestionsPluginType';
import { AgentVendorIcon, hasAgentVendorIcon } from './AgentVendorIcon';

// Matches `Avatar`'s own internal hexagon clip-path (`@atlaskit/avatar/avatar-content`) — duplicated
// per the styling standard's "no cross-file styles" rule.
const AGENT_AVATAR_CLIP_PATH =
	'polygon(45% 1.33975%, 46.5798% 0.60307%, 48.26352% 0.15192%, 50% 0%, 51.73648% 0.15192%, 53.4202% 0.60307%, 55% 1.33975%, 89.64102% 21.33975%, 91.06889% 22.33956%, 92.30146% 23.57212%, 93.30127% 25%, 94.03794% 26.5798%, 94.48909% 28.26352%, 94.64102% 30%, 94.64102% 70%, 94.48909% 71.73648%, 94.03794% 73.4202%, 93.30127% 75%, 92.30146% 76.42788%, 91.06889% 77.66044%, 89.64102% 78.66025%, 55% 98.66025%, 53.4202% 99.39693%, 51.73648% 99.84808%, 50% 100%, 48.26352% 99.84808%, 46.5798% 99.39693%, 45% 98.66025%, 10.35898% 78.66025%, 8.93111% 77.66044%, 7.69854% 76.42788%, 6.69873% 75%, 5.96206% 73.4202%, 5.51091% 71.73648%, 5.35898% 70%, 5.35898% 30%, 5.51091% 28.26352%, 5.96206% 26.5798%, 6.69873% 25%, 7.69854% 23.57212%, 8.93111% 22.33956%, 10.35898% 21.33975%)';

// `Avatar` always draws a `space.025` margin around its own content (so avatars can sit flush in a
// group). Centering the agent avatar within `stack`'s exact `space.300` box cancels that margin out,
// so the visible hex icon — not `Avatar`'s larger margin box — lines up with `stack`'s edges.
const styles = cssMap({
	stack: {
		// Block-level flex, not inline-flex — `Tooltip` wraps this in a plain `div`, and an
		// inline-level child there would sit in a line box whose inherited `line-height` adds
		// leading above the hex avatar, clipping its top. See `SuggestionCard`'s `privateMetadata`.
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
		height: token('space.300'),
		width: token('space.300'),
	},
	// No built-in `Avatar` size is 12px. `small` (24px) scaled by exactly half avoids the blur a
	// less even ratio (e.g. `xxsmall` scaled by 0.75) introduced.
	userBadge: {
		position: 'absolute',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- offsetting `Avatar`'s fixed `space.025` margin, not a design value
		insetBlockEnd: `calc(-1 * ${token('space.025')})`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- offsetting `Avatar`'s fixed `space.025` margin, not a design value
		insetInlineEnd: `calc(-1 * ${token('space.025')})`,
		display: 'flex',
		transform: 'scale(0.5)',
		// `Avatar` always draws a `space.025` margin around its own content, so the anchor for the
		// scale — and the inset above — both offset that margin, keeping the visible avatar (not
		// its margin box) flush with `stack`'s bottom-right corner.
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- offsetting `Avatar`'s fixed `space.025` margin, not a design value
		transformOrigin: `calc(100% - ${token('space.025')}) calc(100% - ${token('space.025')})`,
	},
	// `Avatar`'s hexagon appearance builds its shape from nested divs, each independently clipped to
	// the same polygon at a different padding-adjusted pixel size. The vendor SVG is a bare,
	// full-bleed square with no margin of its own, so it renders flush against the innermost of
	// those clips — any sub-pixel rounding mismatch between that layer and the ones above it can
	// shave a hairline off the shared, very acute top vertex. Clipping this to a `95%` inset (mirrors
	// `SuggestionAvatar`'s `vendorIconClip`) gives that rounding room to land in without exposing
	// anything but the (here, transparent) layer behind it.
	vendorIconClip: {
		position: 'relative',
		height: '95%',
		width: '95%',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values
		clipPath: AGENT_AVATAR_CLIP_PATH,
	},
});

/** Renders the compact Rovo identity used when no agent avatar is available. */
function RovoIdentity(): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<RovoHexIcon label={formatMessage(aiSuggestionsMessages.cardRovoAuthorLabel)} size="small" />
	);
}

function AuthorsTooltip({
	agentName,
	children,
	userName,
}: {
	agentName: string;
	children: React.ReactElement;
	userName?: string;
}): React.JSX.Element {
	const { formatMessage } = useIntl();

	return (
		<Tooltip
			content={
				userName
					? formatMessage(aiSuggestionsMessages.cardAuthorsTooltipLabel, { agentName, userName })
					: agentName
			}
			ignoreTooltipPointerEvents
			position="bottom"
		>
			{children}
		</Tooltip>
	);
}

/** Renders the agent and user identities associated with a suggestion. */
export function SuggestionAgentIdentity({
	agent,
	user,
}: {
	agent?: SuggestionProfile | null;
	user?: SuggestionProfile | null;
}): React.JSX.Element {
	const { formatMessage } = useIntl();
	const agentName = agent?.name?.trim();
	const userName = user?.name?.trim();
	const validAgent =
		agent?.accountId && agentName
			? {
					name: agentName,
					picture: agent.picture?.trim() || undefined,
				}
			: undefined;
	const validUser =
		user?.accountId && userName
			? {
					name: userName,
					picture: user.picture?.trim() || undefined,
				}
			: undefined;

	// A vendor icon is a bare SVG, so `AvatarContent` has to draw the hexagon shape for it. A
	// backend-provided agent avatar brings its own via `Avatar`'s `src`/`appearance` props.
	// `borderColor="transparent"` drops the ring `Avatar` draws by default — the user avatar badge
	// now provides the only visual separation.
	const agentSlot = validAgent ? (
		hasAgentVendorIcon(validAgent.name) ? (
			<Avatar appearance="hexagon" borderColor="transparent" label={validAgent.name} size="small">
				<AvatarContent>
					<span css={styles.vendorIconClip}>
						<AgentVendorIcon agentName={validAgent.name} fallback={<span />} />
					</span>
				</AvatarContent>
			</Avatar>
		) : (
			<Avatar
				appearance="hexagon"
				borderColor="transparent"
				label={validAgent.name}
				name={validAgent.name}
				size="small"
				src={validAgent.picture}
			/>
		)
	) : (
		<Avatar
			appearance="hexagon"
			borderColor="transparent"
			label={formatMessage(aiSuggestionsMessages.cardRovoAuthorLabel)}
			size="small"
		>
			<AvatarContent>
				<RovoIdentity />
			</AvatarContent>
		</Avatar>
	);

	const authorAvatars = (
		<div css={styles.stack}>
			{agentSlot}
			{validUser && (
				<span css={styles.userBadge} data-testid="suggestion-agent-identity-user-badge">
					<Avatar
						label={validUser.name}
						name={validUser.name}
						size="small"
						src={validUser.picture}
					/>
				</span>
			)}
		</div>
	);

	return (
		<AuthorsTooltip
			agentName={validAgent?.name ?? formatMessage(aiSuggestionsMessages.cardRovoAuthorLabel)}
			userName={validUser?.name}
		>
			{authorAvatars}
		</AuthorsTooltip>
	);
}
